// Helper to flatten all services from categories
import { buildOfferPolicyFields } from '../../utils/offerPackagePolicy'

export const getAllServices = (categories) => {
  if (!categories || !Array.isArray(categories)) return []
  return categories.reduce((allServices, category) => {
    if (category.services && Array.isArray(category.services)) {
      return [...allServices, ...category.services]
    }
    return allServices
  }, [])
}

export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return ''
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export const formatDateForDisplay = (date) => {
  if (!date) return ''
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return date
  return dateObj.toISOString().split('T')[0]
}

// Helper to format time from database (HH:MM:SS) or display format
export const formatTimeFromDB = (timeString) => {
  if (!timeString) return ''
  // If it's already in display format (e.g., "9:00 AM")
  if (timeString.includes('AM') || timeString.includes('PM')) {
    return timeString
  }
  // If it's in database format (HH:MM:SS or HH:MM)
  const [hours, minutes] = timeString.split(':')
  const hour24 = parseInt(hours) || 0
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
  const ampm = hour24 >= 12 ? 'PM' : 'AM'
  return `${hour12}:${minutes || '00'} ${ampm}`
}

export const formatTimeForDisplay = (timeSlot) => {
  if (!timeSlot) return ''
  // Handle database time format (HH:MM:SS)
  if (typeof timeSlot === 'string' && timeSlot.includes(':') && !timeSlot.includes(' ')) {
    return formatTimeFromDB(timeSlot)
  }
  // Handle display format object
  const timeStr = timeSlot.slot || timeSlot
  const [time, period] = timeStr.split(' ')
  const [hours, minutes] = time.split(':')
  let hour24 = parseInt(hours)
  if (period === 'PM' && hour24 !== 12) hour24 += 12
  if (period === 'AM' && hour24 === 12) hour24 = 0
  return `${hour24.toString().padStart(2, '0')}:${minutes}:00`
}

/**
 * Duration (minutes) from a service row on a stored appointment (JSON).
 */
const parseServiceDurationMinutesForPricing = (s) => {
  if (typeof s?.duration_minutes === 'number' && s.duration_minutes > 0) {
    return Math.floor(s.duration_minutes)
  }
  const d = s?.duration
  if (typeof d === 'number' && d > 0) return Math.floor(d)
  if (typeof d === 'string') {
    const minM = d.match(/(\d+)\s*min/i)
    if (minM) return parseInt(minM[1], 10)
    const hrM = d.match(/(\d+(?:[.,]\d+)?)\s*hr/i)
    if (hrM) return Math.round(parseFloat(hrM[1].replace(',', '.')) * 60)
    const num = d.match(/(\d+)/)
    if (num) return parseInt(num[1], 10)
  }
  return 30
}

/**
 * When per-service USD is missing/zero (common after reschedule) but `total_amount` is set,
 * split the appointment total across services by duration weight so line items match the total.
 */
export const imputeServiceUsdPricesFromAppointmentTotal = (services, totalAmountUsd) => {
  if (!Array.isArray(services) || services.length === 0) return services
  const total = parseFloat(totalAmountUsd)
  if (!Number.isFinite(total) || total <= 0) return services

  const lineSum = services.reduce((sum, s) => {
    const raw = s?.price_from ?? s?.price
    let n = 0
    if (typeof raw === 'number' && !Number.isNaN(raw)) n = raw
    else if (typeof raw === 'string') {
      const p = parseFloat(String(raw).replace(/[^\d.-]/g, ''))
      n = Number.isNaN(p) ? 0 : p
    }
    return sum + n
  }, 0)

  if (lineSum >= 0.01) return services

  const weights = services.map((s) => Math.max(1, parseServiceDurationMinutesForPricing(s)))
  const wsum = weights.reduce((a, b) => a + b, 0)
  return services.map((s, i) => {
    const share = Math.round(((total * weights[i]) / wsum) * 100) / 100
    return {
      ...s,
      price: share,
      price_from: share,
    }
  })
}

/** Day index for a multi-day deal appointment (services / notes / explicit field). */
export const extractBookingDayNumber = (bookingOrAppointment) => {
  if (!bookingOrAppointment) return null
  const explicit = bookingOrAppointment.day_number
  if (explicit != null && !Number.isNaN(Number(explicit))) {
    return parseInt(explicit, 10)
  }
  const notes = bookingOrAppointment.notes
  if (typeof notes === 'string') {
    const match = notes.match(/\[Day\s+(\d+)\]/i)
    if (match) return parseInt(match[1], 10)
  }
  const services = Array.isArray(bookingOrAppointment.services)
    ? bookingOrAppointment.services
    : []
  const fromServices = services
    .map((s) => (s && s.day_number != null ? parseInt(s.day_number, 10) : null))
    .filter((n) => Number.isFinite(n) && n > 0)
  if (fromServices.length > 0) return Math.min(...fromServices)
  return null
}

/**
 * Group filtered bookings into singles + multi-day deal cards (by group_booking_id).
 * Preserves first-seen order from the input list.
 */
export const groupBookingsForDisplay = (bookings = []) => {
  const list = Array.isArray(bookings) ? bookings : []
  const groupMap = new Map()
  const order = []

  for (const booking of list) {
    const gid = booking?.group_booking_id
    if (!gid) {
      order.push({ kind: 'single', id: booking.id, booking })
      continue
    }
    if (!groupMap.has(gid)) {
      groupMap.set(gid, [])
      order.push({ kind: 'deal', id: gid })
    }
    groupMap.get(gid).push(booking)
  }

  return order.map((entry) => {
    if (entry.kind === 'single') {
      return entry
    }
    const days = [...(groupMap.get(entry.id) || [])].sort((a, b) => {
      const da = extractBookingDayNumber(a)
      const db = extractBookingDayNumber(b)
      if (da != null && db != null && da !== db) return da - db
      return String(a.date || '').localeCompare(String(b.date || ''))
    })

    const labeledDays = days.map((day, index) => ({
      ...day,
      day_number: extractBookingDayNumber(day) || index + 1,
    }))

    const packageTotal = labeledDays.reduce(
      (sum, d) => sum + (Number(d.totalAmount ?? d.total_amount) || 0),
      0
    )
    const offerId = labeledDays.find((d) => d.offer_id)?.offer_id || null
    const isPackageOffer = !!offerId
    const offerTitle =
      labeledDays.find((d) => d.offer_title)?.offer_title ||
      (isPackageOffer ? 'Package deal' : 'Multi-day booking')
    const totalDaysMeta = labeledDays.find((d) => d.offer_total_days)?.offer_total_days
    const totalDays = Math.max(
      Number(totalDaysMeta) || 0,
      labeledDays.length,
      ...labeledDays.map((d) => d.day_number || 0)
    )
    const isRescheduled = labeledDays.some((d) => isRescheduledBooking(d))

    return {
      kind: 'deal',
      id: entry.id,
      groupBookingId: entry.id,
      offerId,
      isPackageOffer,
      offerTitle,
      totalDays,
      packageTotal,
      isRescheduled,
      days: labeledDays,
    }
  })
}

/** Shared group id on booking/appointment objects */
export const getGroupBookingId = (bookingOrAppointment) =>
  bookingOrAppointment?.group_booking_id ||
  bookingOrAppointment?.groupBookingId ||
  bookingOrAppointment?.originalData?.group_booking_id ||
  null

export const getBookingNotesText = (booking) =>
  String(booking?.notes || booking?.originalData?.notes || '')

/** True if this booking was created by a reschedule or superseded by one. */
export const isRescheduledBooking = (booking) => {
  const notes = getBookingNotesText(booking)
  return (
    notes.includes('[RESCHEDULED_FROM:') ||
    notes.includes('[RESCHEDULED_TO:') ||
    notes.includes('[RESCHEDULED_FROM_GROUP:') ||
    notes.includes('[RESCHEDULED_TO_GROUP:')
  )
}

/** Hero/package offer booking (not a plain multi-day duration split). */
export const isOfferDealBooking = (booking) => {
  if (!booking) return false
  const offerId =
    booking.offer_id ||
    booking.offerId ||
    booking.originalData?.offer_id ||
    null
  if (offerId) return true
  const title = booking.offer_title || booking.offerTitle || null
  return !!(title && getGroupBookingId(booking))
}

/**
 * Rebuild deal context for package reschedule so web keeps multi-day deal cards.
 */
export const resolvePackageDealForReschedule = (booking, allBookings = []) => {
  if (!booking) {
    return { isDealGroup: false, siblings: [], deal: null, totalDays: 1, offerId: null, groupId: null }
  }

  const groupId = getGroupBookingId(booking)
  const list = Array.isArray(allBookings) ? allBookings : []
  const siblings = groupId
    ? list.filter((b) => getGroupBookingId(b) === groupId)
    : []
  const siblingDays = siblings.length > 0 ? siblings : [booking]

  const offerId =
    booking.offer_id ||
    booking.offerId ||
    siblingDays.find((d) => d.offer_id)?.offer_id ||
    null
  const offerTitle =
    booking.offer_title ||
    siblingDays.find((d) => d.offer_title)?.offer_title ||
    null

  const metaTotalDays = Math.max(
    Number(booking.offer_total_days) || 0,
    Number(booking.total_days) || 0,
    ...siblingDays.map((d) => Number(d.day_number) || 0),
    siblingDays.length,
  )

  const isDealGroup =
    siblingDays.length > 1 ||
    metaTotalDays > 1 ||
    !!(groupId && (offerId || offerTitle))

  let totalDays = Math.max(metaTotalDays, siblingDays.length, 1)
  if (isDealGroup && totalDays < 2) totalDays = 2

  const deal = isDealGroup
    ? {
        id: offerId,
        title: offerTitle || (offerId ? 'Package deal' : 'Multi-day booking'),
        total_days: totalDays,
        isDurationSplit: !offerId,
        isPackageOffer: !!offerId,
      }
    : null

  return {
    isDealGroup,
    siblings: siblingDays,
    deal,
    totalDays: deal?.total_days || 1,
    offerId,
    groupId,
  }
}

/** All active (or all) rows in the same multi-day deal group */
export const findDealSiblingBookings = (booking, allBookings = [], { includeCancelled = false } = {}) => {
  if (!booking) return []
  const gid = getGroupBookingId(booking)
  const list = Array.isArray(allBookings) ? allBookings : []
  if (!gid) return [booking]
  const siblings = list.filter((b) => {
    if (getGroupBookingId(b) !== gid) return false
    if (includeCancelled) return true
    const status = String(b?.status || '').toLowerCase()
    return !['cancelled', 'canceled'].includes(status)
  })
  return siblings.length > 0 ? siblings : [booking]
}

/** Prefer the day that holds package payment / Day 1 */
export const getDealPrimaryBooking = (siblings = []) => {
  const list = Array.isArray(siblings) ? siblings.filter(Boolean) : []
  if (list.length === 0) return null
  const withTotal = [...list].sort(
    (a, b) =>
      (Number(b.totalAmount ?? b.total_amount) || 0) -
      (Number(a.totalAmount ?? a.total_amount) || 0)
  )
  if ((Number(withTotal[0]?.totalAmount ?? withTotal[0]?.total_amount) || 0) > 0) {
    return withTotal[0]
  }
  const byDay = [...list].sort((a, b) => {
    const da = extractBookingDayNumber(a) || 99
    const db = extractBookingDayNumber(b) || 99
    return da - db
  })
  return byDay[0]
}

const toIsoDateKeySafe = (value) => {
  if (!value) return null
  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (iso) return iso[1]
    // "YYYY-MM-DD at HH:MM" or locale strings
    const atSplit = value.split(' at ')[0]
    const iso2 = String(atSplit).match(/^(\d{4}-\d{2}-\d{2})/)
    if (iso2) return iso2[1]
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const shiftIsoDateKey = (iso, deltaDays) => {
  const key = toIsoDateKeySafe(iso)
  if (!key) return null
  const d = new Date(`${key}T12:00:00`)
  if (Number.isNaN(d.getTime())) return null
  d.setDate(d.getDate() + deltaDays)
  return toIsoDateKeySafe(d)
}

const todayIsoDateKey = () => toIsoDateKeySafe(new Date())

/**
 * When rescheduling one day of a multi-day deal, keep days in sequence:
 * Day N must be after Day N-1 and before Day N+1.
 */
export const getDealRescheduleDateConstraints = (currentBooking, allBookings = []) => {
  if (!currentBooking) return null
  const groupId = currentBooking.group_booking_id
  if (!groupId) return null

  const statusOk = (b) => {
    const s = String(b?.status || '').toLowerCase()
    if (!s || s === 'upcoming' || s === 'confirmed' || s === 'pending' || s === 'completed') {
      return true
    }
    return false
  }

  const groupMembers = (Array.isArray(allBookings) ? allBookings : [])
    .filter((b) => b && b.group_booking_id === groupId && statusOk(b))
    .map((b, index) => ({
      id: b.id,
      dateKey: toIsoDateKeySafe(b.date || b.appointment_date || b.originalData?.date),
      dayNumber: extractBookingDayNumber(b) || null,
      raw: b,
      _idx: index,
    }))
    .filter((m) => m.dateKey)

  if (groupMembers.length < 2) return null

  groupMembers.sort((a, b) => {
    if (a.dayNumber != null && b.dayNumber != null && a.dayNumber !== b.dayNumber) {
      return a.dayNumber - b.dayNumber
    }
    return String(a.dateKey).localeCompare(String(b.dateKey))
  })

  // Fill missing day numbers by sorted order
  groupMembers.forEach((m, i) => {
    if (m.dayNumber == null) m.dayNumber = i + 1
  })

  const current = groupMembers.find((m) => m.id === currentBooking.id)
  if (!current) return null

  const prev = groupMembers
    .filter((m) => m.id !== current.id && m.dayNumber < current.dayNumber)
    .sort((a, b) => b.dayNumber - a.dayNumber)[0]
  const next = groupMembers
    .filter((m) => m.id !== current.id && m.dayNumber > current.dayNumber)
    .sort((a, b) => a.dayNumber - b.dayNumber)[0]

  let minDate = todayIsoDateKey()
  if (prev?.dateKey) {
    const afterPrev = shiftIsoDateKey(prev.dateKey, 1)
    if (afterPrev && afterPrev > minDate) minDate = afterPrev
  }

  const maxDate = next?.dateKey ? shiftIsoDateKey(next.dateKey, -1) : null

  const disabledDates = groupMembers
    .filter((m) => m.id !== current.id && m.dateKey)
    .map((m) => m.dateKey)

  return {
    minDate,
    maxDate,
    disabledDates,
    dayNumber: current.dayNumber,
    totalDays: Math.max(...groupMembers.map((m) => m.dayNumber)),
    previousDate: prev?.dateKey || null,
    nextDate: next?.dateKey || null,
  }
}

// Combine database appointments with local bookings
// Convert database appointments to booking format for display
export const convertAppointmentToBooking = (appointment) => {
  const professionalData = appointment.professional_data || null
  const notes = typeof appointment?.notes === 'string' ? appointment.notes : ''
  const rescheduleTag =
    notes.includes('[RESCHEDULED_FROM:') ||
    notes.includes('[RESCHEDULED_TO:') ||
    notes.includes('[RESCHEDULED_FROM_GROUP:') ||
    notes.includes('[RESCHEDULED_TO_GROUP:')

  // Build services array and preserve per-service professionals
  let services = Array.isArray(appointment.services)
    ? appointment.services.map(s => {
        // Service should be an object with id, name, price, duration
        // Extract duration from service object
        let serviceDuration = null

        if (typeof s === 'object' && s !== null) {
          serviceDuration = parseServiceDurationMinutesForPricing(s)
        }

        // Keep a readable duration label; prefer original string when present
        const durationStr =
          typeof s?.duration === 'string' && s.duration.trim()
            ? s.duration
            : serviceDuration
              ? `${serviceDuration} min`
              : `${appointment.duration_minutes || 60} min`

        const rawPrice = (typeof s === 'object' && s !== null)
          ? (s.price_from ?? s.price)
          : (typeof s === 'number' ? s : 0)

        const parsedPrice = (() => {
          if (typeof rawPrice === 'number' && !Number.isNaN(rawPrice)) return rawPrice
          if (typeof rawPrice === 'string') {
            const n = parseFloat(rawPrice.replace(/[^\d.-]/g, ''))
            return Number.isNaN(n) ? 0 : n
          }
          return 0
        })()

        const baseService = {
          id: s.id || s.service_id || (typeof s === 'object' ? s.id : s),
          name:
            (typeof s === 'string' && s.trim()) ||
            (typeof s?.name === 'string' && s.name.trim()) ||
            (typeof s?.service_name === 'string' && s.service_name.trim()) ||
            (typeof s?.title === 'string' && s.title.trim()) ||
            'Service',
          price: parsedPrice,
          duration: durationStr,
          duration_minutes: serviceDuration || appointment.duration_minutes || 60,
        }

        // Preserve professional, category, and per-service schedule from database service object
        if (typeof s === 'object' && s !== null) {
          baseService.professional = s.professional || null
          baseService.professional_id =
            s.professional_id || (s.professional && s.professional.id) || null
          baseService.price_from = typeof s.price_from === 'number' ? s.price_from : null
          baseService.category_id =
            s.category_id || (s.category && s.category.id) || null
          baseService.category_name =
            s.category_name || (s.category && s.category.name) || s.group || null
          baseService.group = s.group || baseService.category_name || null
          baseService.scheduled_start_time =
            s.scheduled_start_time || s.scheduledStartTime || null
          baseService.scheduled_end_time =
            s.scheduled_end_time || s.scheduledEndTime || null
          baseService.scheduled_date = s.scheduled_date || s.scheduledDate || null
          if (s.day_number != null) {
            baseService.day_number = s.day_number
          }
        }

        return baseService
      })
    : []

  services = appointment.offer_id || appointment.group_booking_id
    ? services
    : imputeServiceUsdPricesFromAppointmentTotal(services, appointment.total_amount)

  // Derive a primary specialist for simple display (fall back to professional_data.primary)
  const primaryProfessional =
    appointment.professional ||
    professionalData?.primary ||
    (Array.isArray(professionalData?.professionals) &&
      professionalData.professionals.length > 0
      ? professionalData.professionals[0]
      : null)

  return {
    id: appointment.id,
    date: appointment.appointment_date,
    time: { slot: formatTimeFromDB(appointment.start_time) },
    services,
    specialist: primaryProfessional
      ? {
          id: primaryProfessional.id,
          name: primaryProfessional.name,
          specialty: primaryProfessional.role || primaryProfessional.specialty
        }
      : null,
    professionalData: professionalData || null,
    rescheduleTag,
    status:
      appointment.status === 'confirmed'
        ? 'upcoming'
        : appointment.status === 'no_show'
        ? 'missed'
        : appointment.status === 'cancelled'
        ? 'cancelled'
        : appointment.status,
    // Preserve notes so reschedule-count and other tags are available in the web UI
    notes: appointment.notes || '',
    duration: appointment.duration_minutes ? `${appointment.duration_minutes} min` : undefined,
    duration_minutes: appointment.duration_minutes || null,
    // Normalize payment fields so refund logic can detect card-paid appointments
    payment_method: appointment.payment_method || 'cash',
    payment_status: appointment.payment_status || 'unpaid',
    paymentMethod: appointment.payment_method || 'cash',
    paymentStatus: appointment.payment_status || 'unpaid',
    payment_intent_id: appointment.payment_intent_id || null,
    // Support / reference identifiers
    reference_id: appointment.reference_id || null,
    payment_reference_id: appointment.payment_reference_id || null,
    totalAmount: appointment.total_amount,
    createdAt: appointment.created_at || appointment.createdAt || null,
    created_at: appointment.created_at || appointment.createdAt || null,
    updatedAt: appointment.updated_at || appointment.updatedAt || null,
    updated_at: appointment.updated_at || appointment.updatedAt || null,
    statusUpdatedAt: appointment.updated_at || appointment.updatedAt || null,
    review: appointment.review || null, // Preserve review data if exists
    reviews: Array.isArray(appointment.reviews) ? appointment.reviews : [],
    admin_cancellation_reason: appointment.admin_cancellation_reason || null,
    salon_id: appointment.salon_id || appointment.salonId || null,
    salonId: appointment.salon_id || appointment.salonId || null,
    offer_id: appointment.offer_id || null,
    group_booking_id: appointment.group_booking_id || null,
    offer_title:
      appointment.offer_title ||
      appointment.offer?.title ||
      appointment.hero_offers?.title ||
      null,
    offer_total_days:
      appointment.offer_total_days ||
      appointment.offer?.total_days ||
      appointment.hero_offers?.total_days ||
      null,
    starts_at: appointment.hero_offers?.starts_at || appointment.starts_at || null,
    ends_at: appointment.hero_offers?.ends_at || appointment.ends_at || null,
    ...buildOfferPolicyFields({
      ...(appointment.hero_offers || {}),
      ...(appointment.offer || {}),
      starts_at: appointment.hero_offers?.starts_at || appointment.starts_at || null,
      ends_at: appointment.hero_offers?.ends_at || appointment.ends_at || null,
      allowed_payment_methods:
        appointment.hero_offers?.allowed_payment_methods ||
        appointment.allowed_payment_methods ||
        appointment.offer?.allowed_payment_methods,
      payment_methods:
        appointment.hero_offers?.payment_methods ||
        appointment.payment_methods ||
        appointment.offer?.payment_methods,
      cancel_allowed:
        appointment.hero_offers?.cancel_allowed ??
        appointment.cancel_allowed ??
        appointment.offer?.cancel_allowed,
      reschedule_allowed:
        appointment.hero_offers?.reschedule_allowed ??
        appointment.reschedule_allowed ??
        appointment.offer?.reschedule_allowed,
      cancel_policy_override:
        appointment.hero_offers?.cancel_policy_override ??
        appointment.cancel_policy_override ??
        appointment.offer?.cancel_policy_override,
      reschedule_policy_override:
        appointment.hero_offers?.reschedule_policy_override ??
        appointment.reschedule_policy_override ??
        appointment.offer?.reschedule_policy_override,
      allow_cancel: appointment.hero_offers?.allow_cancel ?? appointment.allow_cancel,
      allow_reschedule: appointment.hero_offers?.allow_reschedule ?? appointment.allow_reschedule,
      override_cancel_policy:
        appointment.hero_offers?.override_cancel_policy ?? appointment.override_cancel_policy,
      override_reschedule_policy:
        appointment.hero_offers?.override_reschedule_policy ?? appointment.override_reschedule_policy,
    }),
    day_number: extractBookingDayNumber(appointment),
  }
}

// Helper to parse time string to Date object
export const parseAppointmentDateTime = (booking) => {
  if (!booking?.date) return null

  const dateRaw = String(booking.date).trim()
  // Prefer local Y-M-D construction to avoid UTC day-shift on ISO dates
  const iso = dateRaw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  let appointmentDate
  if (iso) {
    appointmentDate = new Date(
      Number(iso[1]),
      Number(iso[2]) - 1,
      Number(iso[3]),
      0,
      0,
      0,
      0,
    )
  } else {
    appointmentDate = new Date(dateRaw)
  }
  if (Number.isNaN(appointmentDate.getTime())) return null

  if (booking.time) {
    const timeStr = String(
      (typeof booking.time === 'object'
        ? booking.time.slot || booking.time.time || booking.time.label
        : booking.time) || '',
    ).trim()
    if (timeStr) {
      const ampm = timeStr.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)$/i)
      if (ampm) {
        let hour24 = parseInt(ampm[1], 10) || 0
        const minutes = parseInt(ampm[2] || '0', 10) || 0
        const ap = ampm[4].toLowerCase()
        if (ap === 'pm' && hour24 !== 12) hour24 += 12
        if (ap === 'am' && hour24 === 12) hour24 = 0
        appointmentDate.setHours(hour24, minutes, 0, 0)
      } else {
        const parts = timeStr.split(':')
        const hour24 = parseInt(parts[0], 10)
        const minutes = parseInt(parts[1] || '0', 10) || 0
        if (!Number.isNaN(hour24)) {
          appointmentDate.setHours(hour24, minutes, 0, 0)
        }
      }
    }
  }

  return appointmentDate
}

/**
 * Unique professionals on a booking who should receive separate reviews.
 * Merges per-service assignments and professionalData (needed for offers/packages).
 */
export const getBookingProfessionalsForReview = (booking) => {
  if (!booking) return []

  const byId = new Map()
  const addProfessional = (prof, appointmentId = null) => {
    if (!prof) return
    const id = prof.id != null ? String(prof.id) : ''
    if (!id || id === 'any' || id === 'null' || id === 'undefined') return
    const existing = byId.get(id)
    if (existing) {
      if (appointmentId && !existing.appointmentIds.includes(appointmentId)) {
        existing.appointmentIds.push(appointmentId)
      }
      if ((!existing.name || existing.name === 'Professional') && (prof.name || prof.full_name)) {
        existing.name = prof.name || prof.full_name
      }
      return
    }
    byId.set(id, {
      id,
      name: prof.name || prof.full_name || 'Professional',
      appointmentIds: appointmentId ? [appointmentId] : [],
    })
  }

  const appointmentId = booking.id || null
  const services = Array.isArray(booking.services) ? booking.services : []
  services.forEach((service) => {
    if (service?.professional) {
      addProfessional(service.professional, appointmentId)
    }
    if (service?.professional_id) {
      addProfessional(
        {
          id: service.professional_id,
          name: service.professional?.name || service.professional_name || service.professionalName || 'Professional',
        },
        appointmentId
      )
    }
  })

  const list = Array.isArray(booking.professionalData?.professionals)
    ? booking.professionalData.professionals
    : Array.isArray(booking.professional_data?.professionals)
      ? booking.professional_data.professionals
      : []
  list.forEach((p) => addProfessional(p, appointmentId))

  const ids = Array.isArray(booking.professionalData?.professionalIds)
    ? booking.professionalData.professionalIds
    : Array.isArray(booking.professional_data?.professionalIds)
      ? booking.professional_data.professionalIds
      : []
  ids.forEach((id) => addProfessional({ id, name: 'Professional' }, appointmentId))

  addProfessional(booking.professionalData?.primary || booking.professional_data?.primary, appointmentId)
  addProfessional(booking.specialist, appointmentId)
  addProfessional(booking.professional, appointmentId)

  if (booking.professional_id) {
    addProfessional(
      {
        id: booking.professional_id,
        name: booking.specialist?.name || booking.professional?.name || 'Professional',
      },
      appointmentId
    )
  }

  return Array.from(byId.values())
}

/**
 * Unique professionals across all days of a package/offer booking.
 */
export const getDealProfessionalsForReview = (days = []) => {
  const byId = new Map()
  ;(days || []).forEach((day) => {
    getBookingProfessionalsForReview(day).forEach((prof) => {
      const existing = byId.get(prof.id)
      if (!existing) {
        byId.set(prof.id, {
          id: prof.id,
          name: prof.name,
          appointmentIds: [...(prof.appointmentIds || (day.id ? [day.id] : []))],
        })
        return
      }
      ;(prof.appointmentIds || (day.id ? [day.id] : [])).forEach((aid) => {
        if (aid && !existing.appointmentIds.includes(aid)) {
          existing.appointmentIds.push(aid)
        }
      })
      if ((!existing.name || existing.name === 'Professional') && prof.name) {
        existing.name = prof.name
      }
    })
  })
  return Array.from(byId.values())
}

export const getDealReviewKey = (groupBookingId) =>
  groupBookingId ? `deal:${groupBookingId}` : null

const isCancelledLikeStatus = (status) => {
  const s = String(status || '').toLowerCase()
  return ['cancelled', 'canceled', 'no_show', 'missed'].includes(s)
}

/**
 * Package/offer reviews only after every active visit day is completed.
 * Pass the FULL group (siblings from allBookings), not only completed-tab days.
 */
export const areAllPackageVisitsCompleted = (groupDays = [], { totalDays = null } = {}) => {
  const days = Array.isArray(groupDays) ? groupDays.filter(Boolean) : []
  if (days.length === 0) return false

  const expected = Math.max(
    Number(totalDays) || 0,
    ...days.map((d) => Number(d.offer_total_days || d.offerTotalDays || d.total_days) || 0),
    days.length,
  )

  const actionable = days.filter((d) => !isCancelledLikeStatus(d?.status))
  if (actionable.length === 0) return false

  // Multi-day packages require at least 2 visits before review unlocks
  if (expected >= 2 && actionable.length < 2) return false
  if (expected >= 2 && actionable.length < expected) return false

  return actionable.every((d) => String(d?.status || '').toLowerCase() === 'completed')
}

// Helper to get display string for professionals (supports multi-professional bookings)
export const getBookingProfessionalDisplay = (booking) => {
  if (!booking) return 'N/A'

  const professionalsList = Array.isArray(booking.professionalData?.professionals)
    ? booking.professionalData.professionals
    : []

  // If admin reassigns a single-service appointment, the canonical assignment is the primary professional.
  // Services payload may still contain the old professional (stale), so prefer primary in that case.
  const isSingleService = Array.isArray(booking.services) && booking.services.length === 1
  if (isSingleService && booking.professionalData?.primary?.name) {
    return booking.professionalData.primary.name
  }

  // Collect names from BOTH:
  // - professionalData.professionals (DB-level mapping)
  // - booking.services[].professional (service-level mapping; may include "Any Specialist")
  const serviceProfessionals = Array.isArray(booking.services)
    ? booking.services
        .map(s => s && (s.professional?.name || s.professional_name))
        .filter(Boolean)
    : []

  // IMPORTANT: If we can derive professionals from the services list, treat that as the source of truth.
  // This prevents showing stale/previous professionals that might still exist in professionalData
  // after an admin changes professional assignment.
  const hasServiceProfessionals = serviceProfessionals.length > 0

  const names = hasServiceProfessionals
    ? Array.from(new Set(serviceProfessionals))
    : Array.from(new Set(professionalsList.map(p => p && p.name).filter(Boolean)))

  if (names.length > 0) {
    return names.join(', ')
  }

  return (
    booking.professionalData?.primary?.name ||
    (professionalsList[0] && professionalsList[0].name) ||
    booking.specialist?.name ||
    'N/A'
  )
}

// Helper to check if appointment is expired (past date/time)
export const isAppointmentExpired = (booking) => {
  const appointmentDateTime = parseAppointmentDateTime(booking)
  if (!appointmentDateTime) return false
  
  const now = new Date()
  return appointmentDateTime < now
}

// Helper to check if appointment is available for rescheduling/cancellation
// Note: Hardcoded time restrictions have been removed - now only checks if appointment is in the future
export const isAppointmentAtLeastOneHourAway = (booking) => {
  const appointmentDateTime = parseAppointmentDateTime(booking)
  if (!appointmentDateTime) return false
  
  const now = new Date()
  // Allow rescheduling/cancellation if appointment is in the future
  return appointmentDateTime > now
}

export const calculateBookingTotal = (booking) => {
  // Use totalAmount from database if available, otherwise calculate from services
  if (booking.totalAmount) {
    return parseFloat(booking.totalAmount)
  }
  return booking.services.reduce((sum, service) => sum + parseFloat(service.price || 0), 0)
}

/** Subtotal / tax / total for appointment details (tax is baked into total_amount). */
export const getBookingTaxBreakdown = (booking, totalOverride = null) => {
  const services = Array.isArray(booking?.services) ? booking.services : []
  const subtotal = Math.round(
    services.reduce((sum, service) => sum + (parseFloat(service.price || service.price_from || 0) || 0), 0) * 100,
  ) / 100
  const discount = Math.max(
    0,
    Number(booking?.discount || booking?.discount_amount || booking?.discountAmount || 0) || 0,
  )
  const totalRaw =
    totalOverride != null && Number.isFinite(Number(totalOverride))
      ? Number(totalOverride)
      : calculateBookingTotal(booking)
  const total = Math.round(Math.max(0, Number(totalRaw) || 0) * 100) / 100
  const afterDiscount = Math.max(0, subtotal - discount)
  let tax = Math.round((total - afterDiscount) * 100) / 100
  if (tax < 0.01) tax = 0
  if (tax > total) tax = 0
  return { subtotal, discount, tax, total, afterDiscount }
}

export const calculateBookingDuration = (booking) => {
  const totalMinutes = (booking.services || []).reduce((sum, service) => {
    const minutes =
      (typeof service.duration_minutes === 'number' && service.duration_minutes > 0
        ? service.duration_minutes
        : parseServiceDuration(service.duration || service)) || 0
    return sum + minutes
  }, 0)
  return totalMinutes
}

// Helper to parse service duration (handles "50 min", "2 hr", "1 hr 30 mins", numbers)
export const parseServiceDuration = (duration) => {
  if (!duration && duration !== 0) return 0
  if (typeof duration === 'number') return Math.max(0, Math.floor(duration))
  if (typeof duration === 'object') {
    return parseServiceDurationMinutesForPricing(duration)
  }
  if (typeof duration === 'string') {
    const first = duration.split('-')[0].trim()
    let minutes = 0
    const hrMatch = first.match(/(\d+(?:[.,]\d+)?)\s*hr/i)
    const minMatch = first.match(/(\d+)\s*min/i)
    if (hrMatch) minutes += Math.round(parseFloat(hrMatch[1].replace(',', '.')) * 60)
    if (minMatch) minutes += parseInt(minMatch[1], 10)
    if (!hrMatch && !minMatch) {
      const match = first.match(/(\d+)/)
      if (match) minutes += parseInt(match[1], 10)
    }
    return minutes
  }
  return 0
}

// Helper to extract start time from booking time (handles both slot format and plain time)
export const extractStartTime = (bookingTime) => {
  if (!bookingTime) return null
  const timeStr = bookingTime.slot || bookingTime || ''
  if (!timeStr) return null
  
  // Handle slot format like "9:00 AM - 9:45 AM"
  if (timeStr.includes(' - ')) {
    return timeStr.split(' - ')[0].trim()
  }
  
  // Handle plain time format like "9:00 AM"
  return timeStr.trim()
}

// Helper to convert time string to minutes from midnight
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0
  const [time, period] = timeStr.split(' ')
  const [hours, minutes] = time.split(':')
  let hour24 = parseInt(hours) || 0
  if (period === 'PM' && hour24 !== 12) hour24 += 12
  if (period === 'AM' && hour24 === 12) hour24 = 0
  
  return hour24 * 60 + (parseInt(minutes) || 0)
}

// Helper to convert minutes from midnight to time string
export const minutesToTime = (minutes) => {
  const totalMinutes = minutes % (24 * 60)
  const hour24 = Math.floor(totalMinutes / 60)
  const min = totalMinutes % 60
  
  const period = hour24 >= 12 ? 'PM' : 'AM'
  const displayHour = hour24 > 12 ? hour24 - 12 : (hour24 === 0 ? 12 : hour24)
  
  return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`
}

// Calculate service start and end times based on cumulative duration
export const calculateServiceTimeRange = (bookingTime, cumulativeDuration, serviceDuration) => {
  if (!bookingTime || !serviceDuration) return { start: '', end: '' }
  
  // Extract the base start time from booking
  const baseStartTimeStr = extractStartTime(bookingTime)
  if (!baseStartTimeStr) return { start: '', end: '' }
  
  // Convert base start time to minutes
  const baseStartMinutes = timeToMinutes(baseStartTimeStr)
  
  // Calculate this service's start time (base + cumulative duration)
  const serviceStartMinutes = baseStartMinutes + cumulativeDuration
  const serviceStartTime = minutesToTime(serviceStartMinutes)
  
  // Calculate this service's end time (start + service duration)
  const serviceEndMinutes = serviceStartMinutes + serviceDuration
  const serviceEndTime = minutesToTime(serviceEndMinutes)
  
  return { start: serviceStartTime, end: serviceEndTime }
}

const normalizeStoredServiceTime = (raw) => {
  if (raw == null || raw === '') return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (s.includes('AM') || s.includes('PM')) return s
  // HH:MM or HH:MM:SS
  const hm = s.length >= 5 ? s.slice(0, 5) : s
  return formatTimeFromDB(hm.includes(':') ? `${hm}:00` : s)
}

/**
 * Prefer per-service scheduled times (book-by-service-time) when present;
 * otherwise stack from the appointment start time.
 */
export const getServiceDisplayTimeRange = (
  service,
  bookingTime,
  cumulativeDuration,
  serviceDuration
) => {
  const rawStart = service?.scheduled_start_time || service?.scheduledStartTime
  if (rawStart) {
    const startNorm = normalizeStoredServiceTime(rawStart)
    const rawEnd = service?.scheduled_end_time || service?.scheduledEndTime
    if (rawEnd) {
      return { start: startNorm, end: normalizeStoredServiceTime(rawEnd) }
    }
    const startMinutes = timeToMinutes(startNorm)
    const dur =
      serviceDuration ||
      service?.duration_minutes ||
      parseServiceDuration(service?.duration || service) ||
      0
    return { start: startNorm, end: minutesToTime(startMinutes + dur) }
  }
  return calculateServiceTimeRange(bookingTime, cumulativeDuration, serviceDuration)
}

export const getPaymentStatus = (booking) => {
  const status = (booking?.paymentStatus || booking?.payment_status || '').toLowerCase()
  const paymentMethod = booking?.paymentMethod || booking?.payment_method || 'cash'
  
  if (status === 'refunded') {
    return 'Refunded'
  }
  
  if (status === 'paid') {
    // Show payment method for paid status
    return `Paid (${paymentMethod === 'card' ? 'Card' : 'Cash'})`
  }
  
  // For unpaid status, show payment method
  return `Unpaid (${paymentMethod === 'card' ? 'Card' : 'Cash'})`
}

/** Tailwind classes for payment badge — aligns with mobile (emerald paid, red unpaid, amber refunded). */
export const getPaymentStatusBadgeClass = (booking) => {
  const status = String(booking?.paymentStatus || booking?.payment_status || '').toLowerCase()
  if (status === 'refunded') {
    return 'text-amber-800 bg-amber-50 border border-amber-200'
  }
  if (status === 'paid') {
    return 'text-emerald-800 bg-emerald-100'
  }
  return 'text-red-800 bg-red-50'
}

export const buildNotificationFromBooking = (booking) => {
  if (!booking) return null
  const services = Array.isArray(booking.services) ? booking.services : []
  const primaryService =
    (typeof services[0] === 'string' && services[0]) ||
    (typeof services[0]?.name === 'string' && services[0].name) ||
    'Selected service'
  const extraCount = services.length > 1 ? ` +${services.length - 1} more` : ''
  const specialistName = getBookingProfessionalDisplay(booking) || 'our specialist'
  const dateLabel = formatDateForDisplay(booking.date)
  const timeSlot = booking.time?.slot || booking.time || ''
  const baseDescription = `${primaryService}${extraCount} with ${specialistName}`
  const timestamp = booking.statusUpdatedAt || booking.updatedAt || booking.createdAt || booking.date || new Date().toISOString()

  let type = 'appointment_booked'
  let title = 'Appointment Booked'
  let message = `Your appointment for ${baseDescription} is confirmed for ${dateLabel}${timeSlot ? ` at ${timeSlot}` : ''}.`

  if (booking.status === 'cancelled') {
    type = 'appointment_cancelled'
    title = 'Appointment Cancelled'
    message = `${baseDescription} on ${dateLabel}${timeSlot ? ` at ${timeSlot}` : ''} was cancelled.`
    const acr =
      typeof booking.admin_cancellation_reason === 'string'
        ? booking.admin_cancellation_reason.trim()
        : ''
    if (acr) {
      message += `\n\nSalon message: ${acr}`
    }
  } else if (booking.status === 'completed') {
    type = 'appointment_completed'
    title = 'Appointment Completed'
    message = `You completed ${baseDescription} on ${dateLabel}${timeSlot ? ` at ${timeSlot}` : ''}.`
  }

  return {
    id: `${booking.id}-${booking.status}`,
    bookingId: booking.id,
    relatedId: booking.id,
    type,
    rawType: type,
    title,
    message,
    timestamp,
  }
}
